/**
 * @file React hook — useReliabilityDropdowns.ts: form/state logic for contributor dashboard.
 */

import { useCallback, useEffect, useState } from "react";
import axiosClient from "../../../../../api/axiosClient";
import { DropdownOption } from "../../../../../interfaces/dropdownInterfaces";

const extractDropdownOptions = (payload: any): DropdownOption[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.options)) return payload.options;
  if (Array.isArray(payload.data?.options)) return payload.data.options;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

/** Hook: use Reliability Dropdowns. */
export const useReliabilityDropdowns = () => {
  const [dataReviewOptions, setDataReviewOptions] = useState<DropdownOption[]>([]);
  const [dataCompletenessOptions, setDataCompletenessOptions] = useState<DropdownOption[]>([]);
  const [dataAcquisitionOptions, setDataAcquisitionOptions] = useState<DropdownOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDropdowns = useCallback(async () => {
    try {
      // Data Review id is 20
      // Data Completeness id is 19
      // Data Acquisition id is 21
      const [reviewRes, completenessRes, acquisitionRes] = await Promise.all([
        axiosClient.get("/dropdowns/20"),
        axiosClient.get("/dropdowns/19"),
        axiosClient.get("/dropdowns/21"),
      ]);
      setDataReviewOptions(extractDropdownOptions(reviewRes.data));
      setDataCompletenessOptions(extractDropdownOptions(completenessRes.data));
      setDataAcquisitionOptions(extractDropdownOptions(acquisitionRes.data));
      setError(null);
    } catch (err) {
      console.error("Failed to load dropdown options", err);
      setDataReviewOptions([]);
      setDataCompletenessOptions([]);
      setDataAcquisitionOptions([]);
      setError("Failed to load reliability dropdowns");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDropdowns();
  }, [loadDropdowns]);

  return {
    dataReviewOptions,
    dataCompletenessOptions,
    dataAcquisitionOptions,
    loading,
    error,
    refetch: loadDropdowns,
  };
};

